import {useEffect, useState} from 'react'
import {Stack} from 'react-bootstrap'
import {useCSVReader} from 'react-papaparse'
import uploadIcon from '../../../_metronic/images/FileUpload.svg'
import uuid from 'react-uuid'
import {State} from 'country-state-city'

type UploadUtilityCSVProps = {
  btnDisabled?: boolean
  setBtnDisabled?: any
  fileUploadErr: boolean
  setFileUploadErr: any
  setManualDisable?: any
  arrayHelpers: any
  setUtilityData: any
  originalutilityData: any
  featureFlags: boolean
}

const UploadUtilityCSV = ({
  btnDisabled,
  setBtnDisabled,
  fileUploadErr,
  setFileUploadErr,
  setManualDisable,
  arrayHelpers,
  setUtilityData,
  originalutilityData,
  featureFlags,
}: UploadUtilityCSVProps) => {
  const [csvValidationMsg, setCsvValidationMsg] = useState<string>('')
  const [validationMsg, setValidationMsg] = useState<string>('')
  const [uploadingMessage, setUploadingMessage] = useState(false)
  const {CSVReader} = useCSVReader()
  const [state, setState] = useState<{id: string; value: string}[]>([])

  useEffect(() => {
    const getAllStates = State.getStatesOfCountry('US')
    setState(getAllStates.map((eachState) => ({id: eachState.isoCode, value: eachState.name})))
  }, [])

  return (
    <div className='upload-section'>
      <CSVReader
        maxFiles={1}
        accept='.csv'
        onUploadAccepted={async (results: any) => {
          let canUpload = true
          setValidationMsg('')
          setManualDisable?.(true)
          if (results.data.length === 0) {
            setBtnDisabled?.(true)
            setValidationMsg('Please upload valid data')
            canUpload = false
          }

          const expectedHeaders = [
            'Utility',
            'Utility Pronunciation',
            'Utility Name',
            'Utility DUNS Number',
            'Submit Days',
            'Utility Phone Number',
            'UAN Validation',
            'UAN Regex',
            'UAN Regex Message',
            ...(featureFlags ? ['Meter ID Regex', 'Meter ID Regex Message'] : []),
            'Utility State',
            'Zip Code',
            'Note1',
            'Note1 Message',
            'Note2',
            'Note2 Message',
            'Note3',
            'Note3 Message',
          ]
          const requiredColumns = ['Utility', 'Utility Pronunciation', 'Utility DUNS Number']

          const convertToUtilityDataFormat = (item: any) => {
            const formatted: any = {
              value: item?.Utility,
              utilityDunsNumber: item?.['Utility DUNS Number'],
              utilitySubmitDays: item?.['Submit Days'],
              utilityShortName: item?.['Utility Pronunciation']?.split(' ').join(' '),
              utilityName: item?.['Utility Name'],
              utilityPhoneNumber: item?.['Utility Phone Number'],
              uanValidation: item?.['UAN Validation'],
              regexValidation: item?.['UAN Regex'],
              regexValidationMessage: item?.['UAN Regex Message'],
              meterRegexValidation: item?.['Meter ID'],
              meterRegexValidationMessage: item?.['Meter ID Message'],
              utilityState: (() => {
                const matchedState = state.find(
                  (state) => state.value.toLowerCase() === item?.['Utility State']?.toLowerCase()
                )
                return matchedState ? matchedState.value : item?.['Utility State']
              })(),
              zipcodes: Array.from(
                new Set(
                  item?.['Zip Code']
                    .split(',')
                    .map((zipcode: any) => zipcode.trim())
                    .filter((zipcode: any) => zipcode !== null && zipcode !== '')
                )
              ),
              type: 'UTILITY',
              id: uuid(),
            }
            const notesMetadata: {placeholder: string; message: string}[] = []
            if (item?.['Note1'] || item?.['Note1 Message']) {
              notesMetadata.push({
                placeholder: item?.['Note1'] || '',
                message: item?.['Note1 Message'] || '',
              })
            }

            if (item?.['Note2'] || item?.['Note2 Message']) {
              notesMetadata.push({
                placeholder: item?.['Note2'] || '',
                message: item?.['Note2 Message'] || '',
              })
            }

            if (item?.['Note3'] || item?.['Note3 Message']) {
              notesMetadata.push({
                placeholder: item?.['Note3'] || '',
                message: item?.['Note3 Message'] || '',
              })
            }
            if (notesMetadata.length > 0) {
              formatted.notesMetadata = notesMetadata
            }

            return formatted
          }
          const emptyColumns: string[] = []
          const invalidZipCodes: string[] = []
          const utilityState: string[] = []
          const zipCodeRegex = /^\d{5}$/

          const columnName = Object.keys(results.data[0] || {})
            .map((h) => h.trim())
            .filter((h) => h !== '')
            
          if (columnName.length === expectedHeaders.length) {
            expectedHeaders.forEach((header, index) => {
              if (columnName[index] !== header) {
                canUpload = false
                setCsvValidationMsg('Headers are mismatched. Please upload a valid CSV format')
                setBtnDisabled?.(true)
              } else {
                results.data.forEach((item: any, i: number) => {
                  const UtilityState = state.find(
                    (state) => state.value.toLowerCase() === item?.['Utility State']?.toLowerCase()
                  )
                  if (!UtilityState && item['Utility State'].trim() !== '') {
                    utilityState.push(`${i + 1}`)
                  }
                  requiredColumns.forEach((col: string) => {
                    if (!emptyColumns.includes(col) && (!item[col] || item[col].trim() === '')) {
                      emptyColumns.push(col)
                    }
                  })

                  const zipCodes = item['Zip Code']
                    ? item['Zip Code'].split(',').map((zip: string) => zip.trim())
                    : []

                  zipCodes.forEach((zip: string) => {
                    if (!invalidZipCodes.includes(`${i + 1}`) && !zipCodeRegex.test(zip)) {
                      invalidZipCodes.push(`${i + 1}`)
                    }
                  })
                })
              }
            })
          } else {
            canUpload = false
            setCsvValidationMsg('Please upload a valid format')
            setBtnDisabled?.(true)
          }

          if (emptyColumns.length > 0) {
            setBtnDisabled?.(true)
            setCsvValidationMsg(`${emptyColumns.join(', ')} cannot be empty `)
            canUpload = false
          }
          if (utilityState.length > 0) {
            setBtnDisabled?.(true)
            setCsvValidationMsg(
              `Utility State has invalid characters at row ${utilityState.join(', ')}`
            )
            canUpload = false
          }
          if (invalidZipCodes.length > 0) {
            setBtnDisabled?.(true)
            setCsvValidationMsg(
              `Zip Code has invalid characters at row ${invalidZipCodes.join(', ')}`
            )
            canUpload = false
          }
          const noteMessageLimitExceeded: string[] = []

          results.data.forEach((item: any, index: number) => {
            const noteFields = ['Note1 Message', 'Note2 Message', 'Note3 Message']
            noteFields.forEach((field) => {
              const value = item[field]?.trim()
              if (value && value.length > 30) {
                noteMessageLimitExceeded.push(`${field} at row ${index + 1}`)
              }
            })
          })

          if (noteMessageLimitExceeded.length > 0) {
            setBtnDisabled?.(true)
            setCsvValidationMsg(
              `Note message length exceeded 30 characters for ${noteMessageLimitExceeded.join(
                ', '
              )}`
            )
            canUpload = false
          }

          if (canUpload) {
            const utilityData = results.data.map((item: any) => {
              return convertToUtilityDataFormat(item)
            })
            setUtilityData((prevData: any) => [...prevData, ...utilityData])
            setValidationMsg('')
            setCsvValidationMsg('')
            setUploadingMessage(true)
            setBtnDisabled?.(false)
          }
        }}
        onUploadRejected={async (file: any) => {
          const tempError: any = file[0]?.errors[0]
          if (tempError.code === 'file-invalid-type') {
            setCsvValidationMsg('')
            setFileUploadErr(true)
            setBtnDisabled?.(false)
          }
        }}
        config={{header: true, delimiter: ',', skipEmptyLines: true}}
      >
        {({getRootProps, acceptedFile, ProgressBar, getRemoveFileProps, Remove}: any) => (
          <>
            <div {...getRootProps()}>
              <>
                {acceptedFile ? (
                  <>
                    {uploadingMessage ? (
                      <div className='text-center'>
                        {!validationMsg && (
                          <label className='uploaded-label'>File uploaded successfully</label>
                        )}
                        {validationMsg && (
                          <>
                            <p className='mt-1 text-danger'>{validationMsg}</p>
                            <Stack gap={2} className='align-items-center justify-content-center'>
                              <img
                                src={uploadIcon}
                                alt=''
                                width='40'
                                height='40'
                                className='svg-icon svg-icon-1'
                              />
                            </Stack>
                            <span className='text-muted fw-semibold text-muted d-block fs-7 text-center'>
                              Upload CSV
                            </span>
                          </>
                        )}
                      </div>
                    ) : (
                      <>
                        <p className='mt-1 text-danger'>{csvValidationMsg}</p>
                        <Stack gap={2} className='align-items-center justify-content-center'>
                          <img
                            src={uploadIcon}
                            alt=''
                            width='40'
                            height='40'
                            className='svg-icon svg-icon-1'
                          />
                        </Stack>
                        <span className='text-muted fw-semibold text-muted d-block fs-7 text-center'>
                          Upload CSV
                        </span>
                        {fileUploadErr && (
                          <label className='uploaded-label err-label'>
                            Please upload the CSV file.
                          </label>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Stack gap={2} className='align-items-center justify-content-center'>
                      <img
                        src={uploadIcon}
                        alt=''
                        width='40'
                        height='40'
                        className='svg-icon svg-icon-1'
                      />
                      <span className='text-dark text-hover-primary fs-6'>
                        Drop files here or click to upload
                      </span>
                      <span className='text-muted fw-semibold text-muted d-block fs-7'>
                        Upload CSV
                      </span>
                      {!acceptedFile && fileUploadErr && (
                        <label className='uploaded-label err-label'>
                          Please upload the CSV file.
                        </label>
                      )}
                    </Stack>
                  </>
                )}
              </>
            </div>
          </>
        )}
      </CSVReader>
    </div>
  )
}

export default UploadUtilityCSV
